import type { ObservationOrigin } from '@liza/fhir-utils';

/**
 * A chart marker that says where its reading came from: a reading synced from a device or arriving
 * from the health record is **filled**, one somebody typed in is **hollow**. Color keeps encoding
 * which series a point belongs to, so provenance rides on the fill and nothing competes for hue.
 *
 * A point with no provenance draws filled, which is what every chart did before this existed.
 */
export interface ProvenanceDotProps {
  /** Chart row key holding this point's `ObservationOrigin`. */
  provenanceKey: string;
  radius: number;
  /** The series' color — the stroke always, and the fill for everything but a hand-typed reading. */
  color: string;
  opacity: number;
}

/** Recharts hands a dot renderer a superset of these; typed as a partial so it stays assignable to
 * recharts' `DotType` without importing its internals. `payload` carries our chart row. */
interface DotRenderProps {
  cx?: number;
  cy?: number;
  index?: number;
  payload?: Record<string, unknown>;
}

/**
 * Build the `dot` / `activeDot` renderer for one series. Returns a function rather than an element so
 * the same marker can be used for both, at different radii, the way `AdherenceOccurrenceDot` is.
 */
export function provenanceDot({
  provenanceKey,
  radius,
  color,
  opacity,
}: ProvenanceDotProps): (props: DotRenderProps) => React.ReactElement {
  return function ProvenanceDot({ cx, cy, index, payload }: DotRenderProps): React.ReactElement {
    if (cx === undefined || cy === undefined) return <g key={index} />;
    const origin = payload?.[provenanceKey] as ObservationOrigin | undefined;
    const hollow = origin === 'entered';
    return (
      <circle
        // recharts' own marker class, not a utility — keeps the charts' `.recharts-dot` transition
        // working on a marker they render themselves.
        // eslint-disable-next-line better-tailwindcss/no-unknown-classes
        className="recharts-dot"
        key={index}
        cx={cx}
        cy={cy}
        r={radius}
        fill={hollow ? 'var(--color-background)' : color}
        stroke={color}
        strokeWidth={1.5}
        opacity={opacity}
      />
    );
  };
}
